import { supabase } from '../../supabase';
import type { AppBoleta } from '../../types/planManejo';

// ─── Reporte: Control de Inventario ─────────────────────────────────────────────
// Cruza el REQUERIDO (Σ cantidad_aplicar por producto de las boletas visibles)
// contra el SALDO en inv_productos.stock_actual, y calcula el FALTANTE
// (requerido − saldo). Destaca en rojo los productos con faltante. Es el mismo
// reporte que vive en "Boletas de Aplicación", extraído para reutilizarlo desde
// el Historial de Cédulas. Imprime vía iframe con CSS dedicado (reglas ERP).

export interface ContextoInventario {
  semana?: string;                 // semana buscada (ej. "29-26")
  fechaDesde?: string;             // rango de fechas activo
  fechaHasta?: string;
}

// Estados que NO entran en el consolidado. Las canceladas no consumen inventario.
// Las manuales tampoco: su producto ya se descontó por la boleta física.
function boletaCuenta(b: AppBoleta): boolean {
  return b.id > 0 && b.estado !== 'cancelada' && !b.manual;
}

export async function imprimirControlInventario(
  boletas: AppBoleta[],
  empresaId: number,
  contexto: ContextoInventario,
  empresaNombre: string,
  _empresaCedula: string,
  empresaLogo = '',
): Promise<{ ok: boolean; error?: string }> {
  const incluidas = boletas.filter(boletaCuenta);
  const boletaIds = incluidas.map(b => b.id);
  if (boletaIds.length === 0) {
    return { ok: false, error: 'No hay cédulas ERP (no manuales / no canceladas) en la vista.' };
  }

  // 1. Requerido por producto (batches de 200 ids por el límite de URL de PostgREST).
  const map = new Map<string, { nombre: string; medida: string; requerido: number }>();
  for (let i = 0; i < boletaIds.length; i += 200) {
    const slice = boletaIds.slice(i, i + 200);
    const { data: prods, error } = await supabase
      .from('app_boleta_productos')
      .select('articulo, nombre_producto, medida, cantidad_aplicar, es_agua')
      .in('boleta_id', slice)
      .eq('empresa_id', empresaId);
    if (error) return { ok: false, error: error.message };
    for (const p of (prods ?? [])) {
      if ((p as any).es_agua) continue;         // el agua no es insumo de inventario
      const key = (p as any).articulo;
      if (!key) continue;
      if (!map.has(key)) map.set(key, { nombre: (p as any).nombre_producto, medida: (p as any).medida ?? '', requerido: 0 });
      map.get(key)!.requerido += Number((p as any).cantidad_aplicar ?? 0);
    }
  }

  if (map.size === 0) {
    return { ok: false, error: 'Las cédulas de la vista no tienen productos registrados.' };
  }

  // 2. Saldo actual en inventario para esos códigos.
  const codigos = [...map.keys()];
  const saldoMap = new Map<string, { saldo: number; medidaInv: string }>();
  for (let i = 0; i < codigos.length; i += 200) {
    const slice = codigos.slice(i, i + 200);
    const { data: invProds } = await supabase
      .from('inv_productos')
      .select('codigo, stock_actual, unidad_medida')
      .eq('empresa_id', empresaId)
      .in('codigo', slice);
    for (const ip of (invProds ?? [])) {
      saldoMap.set((ip as any).codigo, { saldo: Number((ip as any).stock_actual ?? 0), medidaInv: (ip as any).unidad_medida ?? '' });
    }
  }

  // 3. Armar el HTML (idéntico al de Boletas de Aplicación).
  const filas = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const semLabel = contexto.semana
    || (contexto.fechaDesde && contexto.fechaHasta ? `${contexto.fechaDesde} / ${contexto.fechaHasta}` : 'Rango visible');
  const fmt = (n: number) => n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const empresa = empresaNombre || 'AGROPECUARIA VASQUEZ Y ZUÑIGA, S.A.';
  const ahora = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const totalReq = filas.reduce((s, [, v]) => s + v.requerido, 0);
  const totalFalt = filas.reduce((s, [cod, v]) => {
    const f = v.requerido - (saldoMap.get(cod)?.saldo ?? 0);
    return s + (f > 0 ? f : 0);
  }, 0);
  const conFaltante = filas.filter(([cod, v]) => (v.requerido - (saldoMap.get(cod)?.saldo ?? 0)) > 0).length;

  const filasBg = filas.map(([cod, v], i) => {
    const inv = saldoMap.get(cod);
    const saldo = inv?.saldo ?? 0;
    const faltante = v.requerido - saldo;
    const empaque = v.medida || inv?.medidaInv || '';
    const rowClass = faltante > 0 ? 'falt-row' : i % 2 === 0 ? '' : 'alt';
    return `<tr class="${rowClass}">
        <td class="cod">${cod}</td>
        <td class="nombre">${v.nombre}</td>
        <td class="emp">${empaque}</td>
        <td class="num saldo">${saldo > 0 ? fmt(saldo) : '<span class="cero">—</span>'}</td>
        <td class="num req">${fmt(v.requerido)}</td>
        <td class="num ${faltante > 0 ? 'falt' : 'ok'}">${faltante > 0 ? fmt(faltante) : '<span class="check">✓</span>'}</td>
      </tr>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8">
<title>Control de Inventario — Semana ${semLabel}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #1a1a2e; background: #fff; padding: 28px 32px; }
  .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 3px solid #1565c0; gap: 16px; }
  .header-logo { width: 64px; min-width: 64px; text-align: center; }
  .header-center { flex: 1; }
  .empresa { font-size: 17px; font-weight: 700; color: #1565c0; letter-spacing: .4px; }
  .modulo  { font-size: 11px; font-weight: 600; color: #1976d2; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
  .header-right { text-align: right; }
  .titulo-doc { font-size: 20px; font-weight: 700; color: #1a1a2e; line-height: 1.1; }
  .semana-badge { display: inline-block; margin-top: 6px; background: #1565c0; color: #fff; font-size: 13px; font-weight: 700; padding: 3px 14px; border-radius: 20px; }
  .fecha-gen { font-size: 9px; color: #888; margin-top: 6px; }
  .kpis { display: flex; gap: 12px; margin-bottom: 18px; }
  .kpi { flex: 1; border-radius: 8px; padding: 10px 14px; }
  .kpi.total  { background: #e3f2fd; border: 1px solid #90caf9; }
  .kpi.falt   { background: #ffebee; border: 1px solid #ef9a9a; }
  .kpi.ok     { background: #e8f5e9; border: 1px solid #a5d6a7; }
  .kpi-val  { font-size: 22px; font-weight: 700; line-height: 1; }
  .kpi.total .kpi-val { color: #1565c0; }
  .kpi.falt  .kpi-val { color: #c62828; }
  .kpi.ok    .kpi-val { color: #2e7d32; }
  .kpi-label { font-size: 10px; color: #555; margin-top: 3px; text-transform: uppercase; letter-spacing: .5px; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #1565c0; }
  thead th { color: #fff; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .8px; padding: 9px 10px; text-align: left; }
  thead th.r { text-align: right; }
  tbody tr { border-bottom: 1px solid #e3f2fd; }
  tbody tr.alt { background: #f8fbff; }
  tbody tr.falt-row { background: #fff8f8; }
  td { padding: 6px 10px; font-size: 11.5px; vertical-align: middle; }
  td.cod   { font-weight: 600; color: #1565c0; width: 72px; font-size: 11px; }
  td.nombre { color: #1a1a2e; }
  td.emp   { color: #666; font-size: 10.5px; width: 56px; }
  td.num   { text-align: right; font-variant-numeric: tabular-nums; width: 100px; }
  td.saldo { color: #555; }
  td.req   { font-weight: 600; color: #1a1a2e; }
  td.falt  { font-weight: 700; color: #c62828; }
  td.ok    { color: #2e7d32; font-size: 14px; text-align: center; }
  .cero    { color: #bbb; }
  .check   { color: #2e7d32; }
  tfoot tr { background: #1565c0; }
  tfoot td { color: #fff; font-weight: 700; font-size: 11px; padding: 8px 10px; }
  tfoot td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tfoot td.falt-tot { color: #ffcdd2; text-align: right; }
  .footer { margin-top: 20px; display: flex; justify-content: space-between; align-items: center; font-size: 9.5px; color: #888; border-top: 1px solid #e0e0e0; padding-top: 10px; }
  @media print {
    body { padding: 0; }
    @page { margin: 14mm 12mm; size: letter portrait; }
    tbody tr.falt-row { background: #fff8f8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    thead tr, tfoot tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head><body>

<div class="header">
  <div class="header-logo">
    ${empresaLogo
      ? `<img src="${empresaLogo}" alt="Logo" style="width:58px;height:58px;object-fit:contain;">`
      : `<div style="width:58px;height:58px;"></div>`
    }
  </div>
  <div class="header-center">
    <div class="empresa">${empresa}</div>
    <div class="modulo">Programación de Aplicaciones en Campo</div>
  </div>
  <div class="header-right">
    <div class="titulo-doc">Control de Inventario</div>
    <div><span class="semana-badge">Semana ${semLabel}</span></div>
    <div class="fecha-gen">Generado: ${ahora}</div>
  </div>
</div>

<div class="kpis">
  <div class="kpi total">
    <div class="kpi-val">${filas.length}</div>
    <div class="kpi-label">Productos requeridos</div>
  </div>
  <div class="kpi falt">
    <div class="kpi-val">${conFaltante}</div>
    <div class="kpi-label">Con faltante</div>
  </div>
  <div class="kpi ok">
    <div class="kpi-val">${filas.length - conFaltante}</div>
    <div class="kpi-label">Cubiertos</div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:72px">Código</th>
      <th>Artículo</th>
      <th style="width:56px">Emp.</th>
      <th class="r" style="width:100px">Saldo</th>
      <th class="r" style="width:100px">Requerido</th>
      <th class="r" style="width:100px">Faltante</th>
    </tr>
  </thead>
  <tbody>${filasBg}</tbody>
  <tfoot>
    <tr>
      <td colspan="3">Total: ${filas.length} productos &bull; ${conFaltante} con faltante</td>
      <td class="num">&nbsp;</td>
      <td class="num">${fmt(totalReq)}</td>
      <td class="falt-tot">${totalFalt > 0 ? fmt(totalFalt) : '✓'}</td>
    </tr>
  </tfoot>
</table>

<div class="footer">
  <span>${empresa} &bull; Semana ${semLabel}</span>
  <span>${ahora}</span>
</div>

</body></html>`;

  const ifr = document.createElement('iframe');
  ifr.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
  document.body.appendChild(ifr);
  const d = ifr.contentDocument || ifr.contentWindow?.document;
  if (d) { d.open(); d.write(html); d.close(); }
  setTimeout(() => {
    if (ifr.contentDocument) ifr.contentDocument.title = `Control de Inventario - Semana ${semLabel}`;
    ifr.contentWindow?.focus();
    ifr.contentWindow?.print();
    setTimeout(() => document.body.removeChild(ifr), 1000);
  }, 500);

  return { ok: true };
}
